import React, { useCallback, useEffect, useMemo, useState } from "react";
import SummaryCards from "../../components/Common/SummaryCards";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AppModal } from "../../components/Common/AppOverlay";
import ActionMenu from "../../components/Common/ActionMenu";
import useAdminDataLifecycle from "../../components/Common/useAdminDataLifecycle";
import CustomDataGrid from "../../components/Common/CustomDataGrid";
import ImportDefinitionViewPage from "./ImportDefinitionViewPage";
import {
  getFormatLabel,
  getSourceSystem,
  sourceSystems,
} from "./importDefinitionConfig";
import { getData, postData } from "../../helpers/url-request";
import {
  BASE_URL,
  DOWNLOAD_IMPORT_DEFINITION_TEMPLATE,
  GET_IMPORT_DEFINITION_LIST,
  SAVE_IMPORT_DEFINITION,
} from "../../helpers/url_helper";
import "../Standards/Standards.scss";

const tone = (status) =>
  status === "Active" ? "active" : status === "Draft" ? "draft" : "archived";

const SourceType = ({ sourceSystem }) => (
  <span className="data-template-list__source">
    <i className="bx bx-layer" />
    {getSourceSystem(sourceSystem).shortLabel}
  </span>
);

const normaliseDefinition = (definition) => ({
  ...definition,
  file: definition.file || definition.templateStorage?.fileName,
  columns: definition.columns ?? definition.mappings?.length ?? 0,
  mapped:
    definition.mapped ??
    definition.mappings?.filter((mapping) => mapping.destination).length ??
    0,
  updated:
    definition.updated || definition.updatedOn || definition.createdOn || "—",
});

const DefinitionActions = ({
  definition,
  onView,
  onEdit,
  onDuplicate,
  onDownload,
  lifecycleActions,
}) => (
  <ActionMenu
    label={definition.name}
    actions={[
      {
        label: "View",
        icon: <i className="bx bx-show" />,
        onSelect: () => onView(definition),
      },
      {
        label: "Edit",
        icon: <i className="bx bx-edit-alt" />,
        onSelect: () => onEdit(definition),
      },
      {
        label: "Duplicate",
        icon: <i className="bx bx-copy" />,
        onSelect: () => onDuplicate(definition),
      },
      {
        label: "Download Template",
        icon: <i className="bx bx-download" />,
        onSelect: () => onDownload(definition),
      },
      ...lifecycleActions,
    ]}
  />
);

const DataTemplateList = ({ refreshRequest = 0 }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [target, setTarget] = useState("all");
  const [source, setSource] = useState("all");
  const [format, setFormat] = useState("all");
  const [definitions, setDefinitions] = useState([]);
  const [viewDefinition, setViewDefinition] = useState(null);
  const loadData = useCallback(
    () =>
      getData(GET_IMPORT_DEFINITION_LIST)
        .then((response) => {
          setDefinitions(
            response?.status
              ? (response.data || []).map(normaliseDefinition)
              : []
          );
        })
        .catch(() => setDefinitions([])),
    []
  );
  const lifecycle = useAdminDataLifecycle(loadData);
  useEffect(() => {
    loadData();
  }, [loadData, refreshRequest]);

  const rows = useMemo(
    () =>
      definitions.filter(
        (definition) =>
          (status === "all" || definition.status.toLowerCase() === status) &&
          (target === "all" || definition.target === target) &&
          (source === "all" || definition.sourceSystem === source) &&
          (format === "all" || definition.formatType === format) &&
          `${definition.name} ${definition.description} ${
            getSourceSystem(definition.sourceSystem).label
          }`
            .toLowerCase()
            .includes(search.toLowerCase())
      ),
    [search, status, target, source, format, definitions]
  );
  const counts = useMemo(
    () => ({
      total: definitions.length,
      active: definitions.filter((definition) => definition.status === "Active")
        .length,
      draft: definitions.filter((definition) => definition.status === "Draft")
        .length,
      sourceTypes: new Set(
        definitions.map((definition) => definition.sourceSystem)
      ).size,
    }),
    [definitions]
  );
  const filtersApplied =
    search ||
    status !== "all" ||
    target !== "all" ||
    source !== "all" ||
    format !== "all";

  const resetFilters = () => {
    setSearch("");
    setStatus("all");
    setTarget("all");
    setSource("all");
    setFormat("all");
  };

  const openCreate = () => navigate("/system/templates/import-definitions/new");

  const openView = (definition) => setViewDefinition(definition);

  const openEdit = (definition) =>
    navigate(`/system/templates/import-definitions/${definition.id}/edit`, {
      state: { definition },
    });

  const duplicateDefinition = async (definition) => {
    const response = await postData(SAVE_IMPORT_DEFINITION, {
      ...definition,
      id: null,
      definitionKey: null,
      name: `${definition.name} Copy`,
      status: "Draft",
      templateStorage: null,
    });
    if (response?.status)
      setDefinitions((current) => [
        ...current,
        normaliseDefinition(response.data),
      ]);
  };

  const downloadDefinition = async (definition) => {
    const toastId = `import-definition-download-${definition.id}`;
    toast.loading(`Preparing ${definition.name} for download…`, {
      toastId,
      autoClose: false,
      closeOnClick: false,
      draggable: false,
    });
    const blobName = definition.templateStorage?.blobName;
    const url =
      definition.templateUrl ||
      (blobName &&
        `${BASE_URL}${DOWNLOAD_IMPORT_DEFINITION_TEMPLATE}?blobName=${encodeURIComponent(
          blobName
        )}`);
    try {
      if (url) {
        const response = await fetch(url, { credentials: "include" });
        if (!response.ok)
          throw new Error("The template file could not be downloaded.");
        const total = Number(response.headers.get("content-length")) || 0;
        const reader = response.body?.getReader();
        let fileBlob;
        if (!reader) fileBlob = await response.blob();
        else {
          const chunks = [];
          let loaded = 0;
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            chunks.push(value);
            loaded += value.byteLength;
            toast.update(toastId, {
              render: total
                ? `Downloading ${definition.name}… ${Math.min(
                    100,
                    Math.round((loaded / total) * 100)
                  )}%`
                : `Downloading ${definition.name}…`,
              isLoading: true,
              autoClose: false,
              closeOnClick: false,
              draggable: false,
            });
          }
          fileBlob = new Blob(chunks, {
            type:
              response.headers.get("content-type") ||
              "application/octet-stream",
          });
        }
        const objectUrl = URL.createObjectURL(fileBlob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download =
          definition.templateStorage?.fileName ||
          definition.file ||
          "import-template.xlsx";
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
      } else {
        const blob = new Blob(
          [
            JSON.stringify(
              {
                name: definition.name,
                sourceSystem: definition.sourceSystem,
                formatType: definition.formatType,
                target: definition.target,
              },
              null,
              2
            ),
          ],
          { type: "application/json" }
        );
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = `${definition.name
          .replace(/[^a-z0-9]+/gi, "-")
          .toLowerCase()}-definition.json`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
      }
      toast.update(toastId, {
        render: `${definition.name} download started.`,
        type: "success",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
        draggable: true,
      });
    } catch (error) {
      toast.update(toastId, {
        render: error.message || "The template file could not be downloaded.",
        type: "error",
        isLoading: false,
        autoClose: 5000,
        closeOnClick: true,
        draggable: true,
      });
    }
  };
  const columns = [
    {
      id: "name",
      header: "Import Definition",
      accessorKey: "name",
      minSize: 235,
      flex: 1.2,
      cell: ({ row, getValue }) => (
        <button
          type="button"
          className="standards-grid__name data-template-list__name"
          onClick={() => openView(row.original)}
        >
          <span>
            <i className="bx bx-transfer-alt" />
          </span>
          {getValue()}
        </button>
      ),
    },
    {
      id: "description",
      header: "Description",
      accessorKey: "description",
      minSize: 290,
      flex: 1.45,
      cell: ({ getValue }) => (
        <span className="standards-grid__description">{getValue()}</span>
      ),
    },
    {
      id: "target",
      header: "Import To",
      accessorKey: "target",
      minSize: 115,
      flex: 0.6,
      cell: ({ getValue }) => (
        <span className="standards-grid__tag">{getValue()}</span>
      ),
    },
    {
      id: "sourceSystem",
      header: "Source",
      accessorKey: "sourceSystem",
      minSize: 130,
      flex: 0.7,
      cell: ({ getValue }) => <SourceType sourceSystem={getValue()} />,
    },
    {
      id: "formatType",
      header: "Format",
      accessorFn: (definition) =>
        getFormatLabel(definition.sourceSystem, definition.formatType),
      minSize: 125,
      flex: 0.65,
      cell: ({ getValue }) => (
        <span className="data-template-list__file">
          <i className="bx bx-spreadsheet" />
          {getValue()}
        </span>
      ),
    },
    {
      id: "mappings",
      header: "Mappings",
      accessorFn: (definition) =>
        `${definition.mapped || 0} / ${definition.columns || 0}`,
      minSize: 105,
      flex: 0.5,
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "status",
      minSize: 96,
      flex: 0.5,
      cell: ({ row, getValue }) => (
        <span
          className={`standards-grid__status standards-grid__status--${tone(
            row.original.status
          )}`}
        >
          {getValue()}
        </span>
      ),
    },
    {
      id: "updated",
      header: "Last Updated",
      accessorKey: "updated",
      minSize: 125,
      flex: 0.65,
    },
    {
      id: "actions",
      header: "Actions",
      accessorFn: () => "",
      minSize: 70,
      flex: 0,
      enableSorting: false,
      enableExport: false,
      cell: ({ row }) => (
        <DefinitionActions
          definition={row.original}
          onView={openView}
          onEdit={openEdit}
          onDuplicate={duplicateDefinition}
          onDownload={downloadDefinition}
          lifecycleActions={lifecycle.actions(
            "import-definition",
            row.original
          )}
        />
      ),
    },
  ];

  return (
    <section className="standards-page__content data-template-list">
      <SummaryCards
        items={[
          [
            "bx-transfer-alt",
            "blue",
            "Total Import Definitions",
            counts.total,
            "Reusable import configurations",
          ],
          [
            "bx-check-circle",
            "green",
            "Active Definitions",
            counts.active,
            `${
              counts.total
                ? Math.round((counts.active / counts.total) * 100)
                : 0
            }% ready to use`,
          ],
          [
            "bx-edit-alt",
            "orange",
            "Draft Definitions",
            counts.draft,
            "Definitions being configured",
          ],
          [
            "bx-data",
            "red",
            "Source Types",
            counts.sourceTypes,
            "PIERS Excel workbooks",
          ],
        ]}
        ariaLabel="Page summary"
      />
      <section className="standards-grid-panel">
        
        <CustomDataGrid
 filterContent={<section
          className="standards-filter-panel import-definition-filters"
          aria-label="Import definition filters"
        >
          <div className="standards-filter-panel__fields">
            <label>
              <span>Search</span>
              <div className="standards-filter-panel__search">
                <i className="bx bx-search" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search import definitions..."
                />
              </div>
            </label>
            <label>
              <span>Import To</span>
              <select
                value={target}
                onChange={(event) => setTarget(event.target.value)}
              >
                <option value="all">All</option>
                {["Clients", "Engagements", "Contacts", "Users"].map(
                  (value) => (
                    <option key={value}>{value}</option>
                  )
                )}
              </select>
            </label>
            <label>
              <span>Source</span>
              <select
                value={source}
                onChange={(event) => setSource(event.target.value)}
              >
                <option value="all">All</option>
                {sourceSystems.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Format</span>
              <select
                value={format}
                onChange={(event) => setFormat(event.target.value)}
              >
                <option value="all">All</option>
                <option value="XLSX">Excel</option>
                <option value="CSV">CSV</option>
                <option value="XML">XML</option>
                <option value="CCH_NATIVE">Native</option>
                <option value="API">API</option>
              </select>
            </label>
            <label>
              <span>Status</span>
              <select
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
          </div>
          <div className="standards-filter-panel__actions">
            <button
              disabled={!filtersApplied}
              type="button"
              onClick={resetFilters}
            >
              <i className="bx bx-reset" /> Reset filters
            </button>
          </div>
        </section>}

          data={rows}
          columns={columns}
          fileName="import-definitions"
          recordLabel="import definitions"
          showToolbar={false}
          initialPageSize={15}
          rowHeight={56}
          columnHeaderHeight={56}
          gridSx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#ffffff !important",
              borderBottom: "1px solid #e7ebf2",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader--sortable":
              { backgroundColor: "#ffffff !important", color: "#52627f" },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#52627f",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: ".2px",
            },
            "& .MuiDataGrid-columnSeparator": { color: "#e7ebf2" },
            "& .MuiDataGrid-cell": {
              fontSize: 13,
              borderBottomColor: "#edf0f5",
              color: "#273957",
            },
            "& .MuiDataGrid-row": { minHeight: "56px !important" },
            "& .MuiDataGrid-footerContainer, & .MuiDataGrid-footerContainer .MuiTypography-root":
              { fontSize: 14, borderTopColor: "#e7ebf2" },
          }}
        />
      </section>
      <AppModal
        fullscreen
        isOpen={Boolean(viewDefinition)}
        toggle={() => setViewDefinition(null)}
        modalClassName="import-definition-view-modal"
        contentClassName="import-definition-view-modal__content"
        aria-label="Import definition preview"
      >
        <ImportDefinitionViewPage
          definition={viewDefinition}
          definitionId={viewDefinition?.id}
          onClose={() => setViewDefinition(null)}
          onEdit={(definition) => {
            setViewDefinition(null);
            openEdit(definition);
          }}
          modal
        />
      </AppModal>
      {lifecycle.dialog}
    </section>
  );
};

export default DataTemplateList;
